import React from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, styled } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { Typography } from '../../../Typography';
import { SPACING, RADIUS, COLORS, NAV_BUTTON_SIZE, ICON_SIZE } from '../../constants';
import { formatMonthLabel } from '../../utils/dateFormatting';

const CalendarHeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${SPACING['2xl']}px ${SPACING['3xl']}px`,
  paddingBottom: `${SPACING.lg}px`,
});

const MonthYearLabelContainer = styled(Box)({
  flex: 1,
  textAlign: 'center',
});

const NavButton = styled(IconButton)({
  width: `${NAV_BUTTON_SIZE}px`,
  height: `${NAV_BUTTON_SIZE}px`,
  padding: `${SPACING.sm}px`,
  borderRadius: `${RADIUS.sm}px`,
  color: COLORS.textQuaternary,
  '&:hover': {
    backgroundColor: COLORS.bgActive,
  },
  '& svg': {
    fontSize: `${ICON_SIZE}px`,
  },
});

const HiddenNavButton = styled(NavButton)({
  opacity: 0,
  pointerEvents: 'none',
});

export type CalendarHeaderProps = {
  month: Dayjs;
  locale: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  showPrevButton?: boolean;
  showNextButton?: boolean;
  isMobile?: boolean;
};

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  month,
  locale,
  onPrevMonth,
  onNextMonth,
  showPrevButton = true,
  showNextButton = true,
}) => {
  return (
    <CalendarHeaderContainer>
      {showPrevButton ? (
        <NavButton onClick={onPrevMonth} aria-label="Previous month">
          <ChevronLeft />
        </NavButton>
      ) : (
        <HiddenNavButton aria-hidden="true">
          <ChevronLeft />
        </HiddenNavButton>
      )}
      <MonthYearLabelContainer>
        <Typography
          variant="body2"
          sx={{
            fontSize: 14,
            lineHeight: '20px',
            fontWeight: 600,
            color: COLORS.textSecondary,
          }}
        >
          {formatMonthLabel(month, locale)}
        </Typography>
      </MonthYearLabelContainer>
      {showNextButton ? (
        <NavButton onClick={onNextMonth} aria-label="Next month">
          <ChevronRight />
        </NavButton>
      ) : (
        <HiddenNavButton aria-hidden="true">
          <ChevronRight />
        </HiddenNavButton>
      )}
    </CalendarHeaderContainer>
  );
};
